/**
 * Users Management Page (Admin Only)
 * 
 * Displays users list with:
 * - Roles and last login information
 * - Create/edit/delete user functionality
 * - Role assignment
 * - Cached data with background refresh
 * - Admin-only access restriction
 * 
 * Requirements: 7.1, 7.2, 7.3, 7.4, 7.5, 7.6
 */

import { useEffect, useState, useMemo, useCallback } from 'react';
import { restClient, User, CreateUserDto, UpdateUserDto, Rider } from '../../api/rest-client';
import { Card } from '../../components/common/Card';
import { Button } from '../../components/common/Button';
import { LoadingSpinner } from '../../components/common/LoadingSpinner';
import { Modal } from '../../components/common/Modal';
import { formatPhoneForDisplay, formatPhoneForStorage } from '../../utils/phone';
import { formatRiderName, getUserRoleBadgeColor } from '../../utils/riderDisplay';
import { composeName, splitName, isValidName } from '../../utils/name';

const ASSOCIATION_ROLES = ['Parent', 'Guardian', 'Spouse', 'Sibling', 'Self', 'Other'];
import PasswordManagementModal from '../../components/admin/PasswordManagementModal';

/**
 * User Form Component
 */
interface UserFormProps {
  user?: User;
  onSubmit: (data: CreateUserDto | UpdateUserDto) => Promise<void>;
  onCancel: () => void;
  isLoading: boolean;
  riders: Rider[];
  currentSeason: string;
}

export function UserForm({ user, onSubmit, onCancel, isLoading, riders, currentSeason }: UserFormProps) {
  const initialNameParts = (): { firstName: string; lastName: string } => {
    if (user?.given_name || user?.family_name) {
      return { firstName: user.given_name || '', lastName: user.family_name || '' };
    }
    const [f, l] = splitName(user?.name || '');
    return { firstName: f, lastName: l };
  };

  const [formData, setFormData] = useState({
    email: user?.email || '',
    firstName: initialNameParts().firstName,
    lastName: initialNameParts().lastName,
    phone: user?.phone ? formatPhoneForDisplay(user.phone) : '',
    groups: user?.groups || [],
  });

  // Rider associations (create mode only). At least one row required.
  const [associations, setAssociations] = useState<Array<{ rider_id: string; role: string }>>([
    { rider_id: '', role: 'Parent' },
  ]);

  const [errors, setErrors] = useState<Record<string, string>>({});

  // Current-season riders selectable in the create form (BR-8)
  // Current-season riders selectable in the create form (BR-8), sorted with
  // Students first, then (legacy) Riders, then Coaches; alphabetical within each.
  const seasonRiders = useMemo(() => {
    const typeRank = (t?: string) => (t === 'Coach' ? 2 : t === 'Student' ? 0 : 1);
    return riders
      .filter(
        r =>
          r.season === currentSeason ||
          (r.seasons_active && r.seasons_active.includes(currentSeason))
      )
      .slice()
      .sort((a, b) => {
        const byType = typeRank(a.type) - typeRank(b.type);
        if (byType !== 0) return byType;
        return (a.name || '').localeCompare(b.name || '');
      });
  }, [riders, currentSeason]);

  // Update form data when user prop changes
  useEffect(() => {
    if (user) {
      const firstName = user.given_name || splitName(user.name || '')[0];
      const lastName = user.family_name || splitName(user.name || '')[1];
      setFormData({
        email: user.email || '',
        firstName,
        lastName,
        phone: user.phone ? formatPhoneForDisplay(user.phone) : '',
        groups: user.groups || [],
      });
    }
  }, [user]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleAssociationChange = (index: number, field: 'rider_id' | 'role', value: string) => {
    setAssociations(prev => prev.map((row, i) => (i === index ? { ...row, [field]: value } : row)));
    if (errors.associations) {
      setErrors(prev => ({ ...prev, associations: '' }));
    }
  };

  const addAssociationRow = () => {
    setAssociations(prev => [...prev, { rider_id: '', role: 'Parent' }]);
  };

  const removeAssociationRow = (index: number) => {
    setAssociations(prev => (prev.length <= 1 ? prev : prev.filter((_, i) => i !== index)));
  };

  const handleGroupToggle = (group: string) => {
    setFormData(prev => {
      // If unchecking, just remove the group
      if (prev.groups.includes(group)) {
        return {
          ...prev,
          groups: prev.groups.filter(g => g !== group),
        };
      }
      
      // If checking, replace all groups with just this one (mutually exclusive)
      return {
        ...prev,
        groups: [group],
      };
    });
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    } else if (!isValidName(formData.firstName)) {
      newErrors.firstName = 'First name is invalid';
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    } else if (!isValidName(formData.lastName)) {
      newErrors.lastName = 'Last name is invalid';
    }

    // Phone is required when creating a new user (existing users may predate
    // this rule, so editing is not blocked).
    if (!user && !formData.phone.trim()) {
      newErrors.phone = 'Phone is required';
    }

    // Rider association is required at CREATION only (BR-4, BR-10).
    if (!user) {
      const selected = associations.filter(a => a.rider_id);
      if (selected.length === 0) {
        newErrors.associations = 'Every user must be associated with at least one rider';
      } else {
        const ids = selected.map(a => a.rider_id);
        if (new Set(ids).size !== ids.length) {
          newErrors.associations = 'Each rider may only be selected once';
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    try {
      // Format phone number for storage if provided
      let formattedPhone: string | undefined = undefined;
      if (formData.phone) {
        try {
          formattedPhone = formatPhoneForStorage(formData.phone);
        } catch (phoneError) {
          setErrors({ phone: phoneError instanceof Error ? phoneError.message : 'Invalid phone number format' });
          return;
        }
      }

      const firstName = formData.firstName.trim();
      const lastName = formData.lastName.trim();
      const baseData = {
        email: formData.email,
        given_name: firstName,
        family_name: lastName,
        name: composeName(firstName, lastName),
        phone: formattedPhone,
        groups: formData.groups,
      };

      if (user) {
        // Update existing user (no rider association requirement on edit)
        await onSubmit({
          username: user.username,
          ...baseData,
        } as UpdateUserDto);
      } else {
        // Create new user — include the required rider association(s)
        await onSubmit({
          ...baseData,
          associations: associations.filter(a => a.rider_id),
        } as CreateUserDto);
      }
    } catch (error) {
      console.error('Form submission error:', error);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Info box for new user creation */}
      {!user && (
        <div className="p-4 bg-blue-50 border border-blue-200 rounded-md">
          <div className="flex">
            <svg className="w-5 h-5 text-blue-400 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
            <div>
              <h4 className="text-sm font-medium text-blue-800">Email Invitation</h4>
              <p className="text-sm text-blue-700 mt-1">
                The user will receive an invitation email with a temporary password. 
                They must click the link to verify their email and set a permanent password.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Email */}
      <div>
        <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
          Email *
        </label>
        <input
          type="email"
          id="email"
          name="email"
          value={formData.email}
          onChange={handleChange}
          disabled={!!user} // Email cannot be changed for existing users
          className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${
            errors.email ? 'border-red-500' : 'border-gray-300'
          } ${user ? 'bg-gray-100 cursor-not-allowed' : ''}`}
          placeholder="user@example.com"
        />
        {errors.email && (
          <p className="mt-1 text-sm text-red-600">{errors.email}</p>
        )}
      </div>

      {/* First name */}
      <div>
        <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-1">
          First name *
        </label>
        <input
          type="text"
          id="firstName"
          name="firstName"
          data-testid="user-form-first-name"
          value={formData.firstName}
          onChange={handleChange}
          className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${
            errors.firstName ? 'border-red-500' : 'border-gray-300'
          }`}
          placeholder="John"
        />
        {errors.firstName && (
          <p className="mt-1 text-sm text-red-600">{errors.firstName}</p>
        )}
      </div>

      {/* Last name */}
      <div>
        <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-1">
          Last name *
        </label>
        <input
          type="text"
          id="lastName"
          name="lastName"
          data-testid="user-form-last-name"
          value={formData.lastName}
          onChange={handleChange}
          className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${
            errors.lastName ? 'border-red-500' : 'border-gray-300'
          }`}
          placeholder="Doe"
        />
        {errors.lastName && (
          <p className="mt-1 text-sm text-red-600">{errors.lastName}</p>
        )}
      </div>

      {/* Phone */}
      <div>
        <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
          Phone {!user && '*'}
        </label>
        <input
          type="tel"
          id="phone"
          name="phone"
          data-testid="user-form-phone"
          value={formData.phone}
          onChange={handleChange}
          className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${
            errors.phone ? 'border-red-500' : 'border-gray-300'
          }`}
          placeholder="(555) 123-4567"
        />
        {errors.phone && (
          <p className="mt-1 text-sm text-red-600">{errors.phone}</p>
        )}
      </div>

      {/* Role Assignment */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Role
        </label>
        <div className="space-y-2">
          <label className="flex items-center">
            <input
              type="radio"
              name="role"
              checked={formData.groups.length === 0}
              onChange={() => setFormData(prev => ({ ...prev, groups: [] }))}
              className="w-4 h-4 text-primary-600 border-gray-300 focus:ring-primary-500"
            />
            <span className="ml-2 text-sm text-gray-700">
              User - View-only access
            </span>
          </label>
          <label className="flex items-center">
            <input
              type="radio"
              name="role"
              checked={formData.groups.includes('coach')}
              onChange={() => handleGroupToggle('coach')}
              className="w-4 h-4 text-primary-600 border-gray-300 focus:ring-primary-500"
            />
            <span className="ml-2 text-sm text-gray-700">
              Coach - Event and rider management
            </span>
          </label>
          <label className="flex items-center">
            <input
              type="radio"
              name="role"
              checked={formData.groups.includes('admin')}
              onChange={() => handleGroupToggle('admin')}
              className="w-4 h-4 text-primary-600 border-gray-300 focus:ring-primary-500"
            />
            <span className="ml-2 text-sm text-gray-700">
              Admin - Full system access
            </span>
          </label>
        </div>
        <p className="mt-1 text-xs text-gray-500">
          Each user can have only one role
        </p>
      </div>

      {/* Rider association — required at creation (BR-4). Hidden when editing. */}
      {!user && (
        <div className="border-t pt-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Associated Rider(s) *
          </label>
          <p className="text-xs text-gray-500 mb-3">
            Every user must be linked to at least one rider. Riders must be created first.
          </p>

          {seasonRiders.length === 0 ? (
            <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 px-3 py-2 rounded text-sm">
              No riders found for the current season. Create a rider first, then create this user.
            </div>
          ) : (
            <div className="space-y-3" data-testid="user-form-associations">
              {associations.map((row, index) => (
                <div key={index} className="flex gap-2 items-start">
                  <select
                    aria-label={`Rider ${index + 1}`}
                    data-testid={`user-form-rider-select-${index}`}
                    value={row.rider_id}
                    onChange={(e) => handleAssociationChange(index, 'rider_id', e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="">-- Select Rider --</option>
                    {seasonRiders.map(rider => (
                      <option key={rider.rider_id} value={rider.rider_id}>
                        {formatRiderName(rider)}
                      </option>
                    ))}
                  </select>
                  <select
                    aria-label={`Relationship ${index + 1}`}
                    data-testid={`user-form-role-select-${index}`}
                    value={row.role}
                    onChange={(e) => handleAssociationChange(index, 'role', e.target.value)}
                    className="w-32 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    {ASSOCIATION_ROLES.map(r => (
                      <option key={r} value={r}>{r}</option>
                    ))}
                  </select>
                  {associations.length > 1 && (
                    <button
                      type="button"
                      data-testid={`user-form-remove-rider-${index}`}
                      onClick={() => removeAssociationRow(index)}
                      className="px-3 py-2 text-sm text-red-600 hover:text-red-800"
                      aria-label={`Remove rider ${index + 1}`}
                    >
                      Remove
                    </button>
                  )}
                </div>
              ))}
              <button
                type="button"
                data-testid="user-form-add-rider"
                onClick={addAssociationRow}
                className="text-sm text-primary-600 hover:text-primary-800"
              >
                + Add another rider
              </button>
            </div>
          )}
          {errors.associations && (
            <p className="mt-2 text-sm text-red-600" data-testid="user-form-associations-error">{errors.associations}</p>
          )}
        </div>
      )}

      {/* Actions */}
      <div className="flex gap-3 pt-4">
        <Button
          type="submit"
          variant="primary"
          disabled={isLoading}
          className="flex-1"
        >
          {isLoading ? 'Saving...' : user ? 'Update User' : 'Create User'}
        </Button>
        <Button
          type="button"
          variant="secondary"
          onClick={onCancel}
          disabled={isLoading}
        >
          Cancel
        </Button>
      </div>
    </form>
  );
}

/**
 * User Card Component
 */
interface UserCardProps {
  user: User;
  onEdit: (user: User) => void;
  onDelete: (user: User) => void;
  onResetPassword: (user: User) => void;
  onAssociateRider: (user: User) => void;
  currentSeason: string;
  allRiders: any[];
}

function UserCard({ user, onEdit, onDelete, onResetPassword, onAssociateRider, currentSeason, allRiders }: UserCardProps) {
  const roleBadge = getUserRoleBadgeColor(user.groups);

  // Check if user has no rider associations
  const hasNoRiders = !user.rider_associations || user.rider_associations.length === 0;
  
  // Check if user has no riders in current season
  const hasNoCurrentSeasonRiders = !hasNoRiders && user.rider_associations && user.rider_associations.every(assoc => {
    const rider = allRiders.find(r => r.rider_id === assoc.rider_id);
    return !rider || rider.season !== currentSeason;
  });
  
  // Show red background if no riders at all OR no riders in current season
  const showRedBackground = hasNoRiders || hasNoCurrentSeasonRiders;

  return (
    <Card className={`hover:shadow-lg transition-shadow ${showRedBackground ? '!bg-red-50' : ''}`}>
      <div className="flex flex-col h-full">
        {/* Role Badge */}
        <div className="mb-3">
          <span className={`inline-block px-2 py-1 text-xs font-semibold ${roleBadge.color} rounded-full`}>
            {roleBadge.text}
          </span>
        </div>

        {/* User Info */}
        <div className="flex-grow space-y-2 mb-4">
          <h3 className={`text-lg font-bold ${showRedBackground ? 'text-red-600' : 'text-gray-900'}`}>
            {user.name || user.given_name || 'No Name'}
          </h3>
          
          <div className="text-sm text-gray-600">
            <div className="flex items-center">
              <svg
                className="w-4 h-4 mr-2 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                />
              </svg>
              <span className="break-all">{user.email}</span>
            </div>
          </div>

          {user.phone && (
            <div className="text-sm text-gray-600">
              <div className="flex items-center">
                <svg
                  className="w-4 h-4 mr-2 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                  />
                </svg>
                <span>{formatPhoneForDisplay(user.phone)}</span>
              </div>
            </div>
          )}

          {/* Last Login */}
          {user.last_login && (
            <div className="text-sm text-gray-600">
              <div className="flex items-center">
                <svg
                  className="w-4 h-4 mr-2 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <span className={
                  user.last_login === 'Never' || 
                  user.last_login === 'Tracking unavailable' || 
                  user.last_login === 'Pending sync' ||
                  user.last_login === 'Error'
                    ? 'text-red-600 font-bold'
                    : ''
                }>
                  Last seen: {user.last_login}
                </span>
              </div>
            </div>
          )}

          {/* Associated Riders */}
          {user.rider_associations && user.rider_associations.length > 0 ? (
            <div className="text-sm text-gray-600 mt-2 pt-2 border-t border-gray-200">
              <div className="font-medium text-gray-700 mb-1">Associated Riders:</div>
              <div className="space-y-1">
                {user.rider_associations.map((assoc, index) => {
                  const rider = allRiders.find((r: any) => r.rider_id === assoc.rider_id);
                  const isCurrentSeason = rider && (
                    rider.season === currentSeason ||
                    (rider.seasons_active && rider.seasons_active.includes(currentSeason))
                  );
                  return (
                    <div key={index} className="text-xs">
                      <span className={`font-medium ${!isCurrentSeason ? 'line-through text-gray-400' : ''}`}>
                        {formatRiderName(assoc)}
                      </span>
                      <span className={!isCurrentSeason ? 'text-gray-400' : 'text-gray-500'}> - {assoc.role}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            <div className="text-sm text-gray-500 italic mt-2 pt-2 border-t border-gray-200">
              No riders associated
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="grid grid-cols-2 gap-2 mt-auto">
          <Button
            variant="secondary"
            size="small"
            onClick={() => onResetPassword(user)}
            className="w-full min-h-[44px]"
          >
            Reset Password
          </Button>
          <Button
            variant="primary"
            size="small"
            onClick={() => onAssociateRider(user)}
            className="w-full min-h-[44px]"
          >
            Associate Rider
          </Button>
          <Button
            variant="secondary"
            size="small"
            onClick={() => onEdit(user)}
            className="w-full min-h-[44px]"
          >
            Edit
          </Button>
          <Button
            variant="danger"
            size="small"
            onClick={() => onDelete(user)}
            className="w-full min-h-[44px]"
          >
            Delete
          </Button>
        </div>
      </div>
    </Card>
  );
}

/**
 * Delete Confirmation Modal
 */
interface DeleteConfirmationProps {
  user: User | null;
  isOpen: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  isLoading: boolean;
}

function DeleteConfirmation({ user, isOpen, onConfirm, onCancel, isLoading }: DeleteConfirmationProps) {
  if (!user) return null;

  return (
    <Modal isOpen={isOpen} onClose={onCancel} title="Delete User">
      <div className="space-y-4">
        <p className="text-gray-700">
          Are you sure you want to delete the user <strong>{user.name || user.email}</strong>?
        </p>
        <p className="text-sm text-red-600">
          This action cannot be undone.
        </p>
        <div className="flex gap-3 pt-4">
          <Button
            variant="danger"
            onClick={onConfirm}
            disabled={isLoading}
            className="flex-1"
          >
            {isLoading ? 'Deleting...' : 'Delete User'}
          </Button>
          <Button
            variant="secondary"
            onClick={onCancel}
            disabled={isLoading}
          >
            Cancel
          </Button>
        </div>
      </div>
    </Modal>
  );
}

/**
 * Users Management Page Component
 * Requirements: 7.1, 7.2, 7.3, 7.4, 7.5, 7.6
 */
export function Users() {
  // State
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentSeason, setCurrentSeason] = useState<string>('');
  
  // Password management state
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  const [selectedUserForPassword, setSelectedUserForPassword] = useState<User | null>(null);
  
  // Rider association state
  const [isAssociateModalOpen, setIsAssociateModalOpen] = useState(false);
  const [riders, setRiders] = useState<any[]>([]);
  const [selectedRiderId, setSelectedRiderId] = useState('');
  const [selectedRole, setSelectedRole] = useState('Parent');
  /**
   * Fetch users with caching (uses global cache from rest-client)
   * Requirements: 7.1, 7.6
   */
  const fetchUsers = useCallback(async () => {
    try {
      setLoading(prev => users.length === 0 ? true : prev); // Only show spinner on first load
      setError(null);

      const seasonConfig = await restClient.getSeasonConfig();
      setCurrentSeason(seasonConfig.currentSeason);

      // Single call — response includes both users and riders
      const startTime = performance.now();
      const response = await restClient.getUsersWithRiders();
      const duration = performance.now() - startTime;
      console.log(`[Users] Fetched ${response.users.length} users + ${response.riders.length} riders in ${Math.round(duration)}ms`);

      setUsers(response.users);
      setRiders(response.riders);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [users.length]);

  // Fetch users on mount
  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // Background refresh every 5 minutes (matches cache TTL)
  useEffect(() => {
    const interval = setInterval(() => {
      fetchUsers();
    }, 300000); // 5 minutes = 300,000ms (was 30,000ms)

    return () => clearInterval(interval);
  }, [fetchUsers]);

  // Refetch when page becomes visible — only if cache is stale (≥5 minutes)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        const cacheTimestamp = restClient.getUsersCacheTimestamp();
        const age = cacheTimestamp ? Date.now() - cacheTimestamp : Infinity;
        if (age >= 300_000) { // 5 minutes
          console.log(`[Users] Visibility change: cache age ${Math.round(age / 1000)}s, refetching`);
          fetchUsers(); // Background refetch, no spinner
        } else {
          console.log(`[Users] Visibility change: cache age ${Math.round(age / 1000)}s, skipping refetch`);
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [fetchUsers]);

  /**
   * Filter users by search query
   * Searches by name, email, username, and associated rider names
   */
  const filteredUsers = useMemo(() => {
    if (!searchQuery.trim()) {
      return users;
    }

    const query = searchQuery.toLowerCase();
    return users.filter(user => {
      // Search by user fields
      const matchesUser = 
        user.name?.toLowerCase().includes(query) ||
        user.given_name?.toLowerCase().includes(query) ||
        user.email.toLowerCase().includes(query) ||
        user.username.toLowerCase().includes(query);
      
      // Search by associated rider names
      const matchesRider = user.rider_associations?.some(assoc =>
        assoc.name.toLowerCase().includes(query)
      );
      
      return matchesUser || matchesRider;
    });
  }, [users, searchQuery]);

  /**
   * Handle create user
   * Requirement 7.3
   */
  const handleCreateUser = useCallback(() => {
    setSelectedUser(null);
    setIsFormModalOpen(true);
  }, []);

  /**
   * Handle edit user
   * Requirement 7.4
   */
  const handleEditUser = useCallback((user: User) => {
    setSelectedUser(user);
    setIsFormModalOpen(true);
  }, []);

  /**
   * Handle delete user
   * Requirement 7.5
   */
  const handleDeleteUser = useCallback((user: User) => {
    setSelectedUser(user);
    setIsDeleteModalOpen(true);
  }, []);

  /**
   * Handle reset password
   * Requirement: Phase 3 - Admin Password Management
   */
  const handleResetPassword = useCallback((user: User) => {
    setSelectedUserForPassword(user);
    setIsPasswordModalOpen(true);
  }, []);

  /**
   * Handle associate rider modal open
   */
  const handleAssociateRider = useCallback(async (user: User) => {
    setSelectedUser(user);
    setIsAssociateModalOpen(true);
    
    if (riders.length > 0) {
      const sortedRiders = [...riders].sort((a, b) => {
        // Students first, then Coaches
        const aIsCoach = a.type === 'Coach' ? 1 : 0;
        const bIsCoach = b.type === 'Coach' ? 1 : 0;
        if (aIsCoach !== bIsCoach) return aIsCoach - bIsCoach;
        // Alphabetical within each type
        return a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
      });
      setRiders(sortedRiders);
    }
  }, [riders]);

  /**
   * Submit rider association
   */
  const handleSubmitAssociation = useCallback(async () => {
    if (!selectedUser || !selectedRiderId || !selectedRole) {
      setError('Please select a rider and role');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const rider = riders.find(r => r.rider_id === selectedRiderId);
      if (!rider) {
        throw new Error('Rider not found');
      }

      let associations: Array<{ user_id: string; role: string }> = [];
      if (rider.associatedUsers) {
        try {
          associations = JSON.parse(rider.associatedUsers);
        } catch (e) {
          throw new Error('Failed to parse existing associations');
        }
      }

      const existingAssociation = associations.find(
        assoc => assoc.user_id === selectedUser.email || assoc.user_id === selectedUser.username
      );
      
      if (existingAssociation) {
        throw new Error(`User is already associated with this rider as ${existingAssociation.role}`);
      }

      associations.push({
        user_id: selectedUser.email,
        role: selectedRole,
      });

      await restClient.updateRider(rider.rider_id, {
        rider_id: rider.rider_id,
        associatedUsers: JSON.stringify(associations),
      });

      setIsAssociateModalOpen(false);
      setSelectedUser(null);
      setSelectedRiderId('');
      setSelectedRole('Parent');
      setIsSubmitting(false);

      alert(`Successfully associated ${selectedUser.name || selectedUser.email} with ${rider.name} as ${selectedRole}`);

      restClient.clearUsersCache();
      restClient.clearDashboardCache();
      fetchUsers();
      
      const ridersData = await restClient.getRiders();
      setRiders(ridersData);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to associate rider. Please try again.';
      setError(errorMessage);
      alert(`Error: ${errorMessage}`);
      setIsSubmitting(false);
    }
  }, [selectedUser, selectedRiderId, selectedRole, riders, fetchUsers]);

  /**
   * Remove rider association
   */
  const handleRemoveAssociation = useCallback(async (user: User, riderId: string) => {
    if (!confirm('Are you sure you want to remove this association?')) {
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const rider = riders.find(r => r.rider_id === riderId);
      if (!rider) {
        const ridersData = await restClient.getRiders();
        const foundRider = ridersData.find(r => r.rider_id === riderId);
        if (!foundRider) {
          throw new Error('Rider not found');
        }
        const sortedRiders = ridersData.sort((a, b) => {
          const aIsCoach = a.type === 'Coach' ? 1 : 0;
          const bIsCoach = b.type === 'Coach' ? 1 : 0;
          if (aIsCoach !== bIsCoach) return aIsCoach - bIsCoach;
          return a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
        });
        setRiders(sortedRiders);
        
        let associations: Array<{ user_id: string; role: string }> = [];
        if (foundRider.associatedUsers) {
          try {
            associations = JSON.parse(foundRider.associatedUsers);
          } catch (e) {
            throw new Error('Failed to parse existing associations');
          }
        }

        const originalLength = associations.length;
        associations = associations.filter(assoc => 
          assoc.user_id !== user.email && assoc.user_id !== user.username
        );

        if (associations.length === originalLength) {
          throw new Error('Association not found');
        }

        await restClient.updateRider(foundRider.rider_id, {
          rider_id: foundRider.rider_id,
          associatedUsers: JSON.stringify(associations),
        });

        if (selectedUser && selectedUser.username === user.username) {
          const updatedAssociations = selectedUser.rider_associations?.filter(
            assoc => assoc.rider_id !== riderId
          ) || [];
          setSelectedUser({
            ...selectedUser,
            rider_associations: updatedAssociations
          });
        }

        setIsSubmitting(false);

        alert(`Successfully removed association between ${user.name || user.email} and ${foundRider.name}`);

        setIsAssociateModalOpen(false);
        setSelectedUser(null);
        setSelectedRiderId('');
        setSelectedRole('Parent');

        restClient.clearUsersCache();
        restClient.clearDashboardCache();
        fetchUsers();
        
        const updatedRidersData = await restClient.getRiders();
        setRiders(updatedRidersData);
      } else {
        let associations: Array<{ user_id: string; role: string }> = [];
        if (rider.associatedUsers) {
          try {
            associations = JSON.parse(rider.associatedUsers);
          } catch (e) {
            throw new Error('Failed to parse existing associations');
          }
        }

        const originalLength = associations.length;
        associations = associations.filter(assoc => 
          assoc.user_id !== user.email && assoc.user_id !== user.username
        );

        if (associations.length === originalLength) {
          throw new Error('Association not found');
        }

        await restClient.updateRider(rider.rider_id, {
          rider_id: rider.rider_id,
          associatedUsers: JSON.stringify(associations),
        });

        if (selectedUser && selectedUser.username === user.username) {
          const updatedAssociations = selectedUser.rider_associations?.filter(
            assoc => assoc.rider_id !== riderId
          ) || [];
          setSelectedUser({
            ...selectedUser,
            rider_associations: updatedAssociations
          });
        }

        setIsSubmitting(false);

        alert(`Successfully removed association between ${user.name || user.email} and ${rider.name}`);

        setIsAssociateModalOpen(false);
        setSelectedUser(null);
        setSelectedRiderId('');
        setSelectedRole('Parent');

        restClient.clearUsersCache();
        restClient.clearDashboardCache();
        fetchUsers();
        
        const refreshedRidersData = await restClient.getRiders();
        setRiders(refreshedRidersData);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to remove association. Please try again.';
      setError(errorMessage);
      alert(`Error: ${errorMessage}`);
      setIsSubmitting(false);
    }
  }, [riders, selectedUser, fetchUsers]);

  /**
   * Submit user form (create or update)
   * Requirements: 7.3, 7.4
   */
  const handleSubmitUser = useCallback(async (data: CreateUserDto | UpdateUserDto) => {
    setIsSubmitting(true);
    setError(null);

    try {
      if (selectedUser) {
        await restClient.updateUser(selectedUser.username, data as UpdateUserDto);
        
        setIsFormModalOpen(false);
        setSelectedUser(null);
        setIsSubmitting(false);
        
        alert('User updated successfully');
        
        restClient.clearUsersCache();
        fetchUsers();
      } else {
        await restClient.createUser(data as CreateUserDto);
        
        setIsFormModalOpen(false);
        setSelectedUser(null);
        setIsSubmitting(false);
        
        alert(`Invitation email sent to ${(data as CreateUserDto).email}.\n\nThe user must check their email to verify and set their password.`);
        
        restClient.clearUsersCache();
        fetchUsers();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save user');
      setIsSubmitting(false);
    }
  }, [selectedUser, fetchUsers]);

  /**
   * Confirm delete user
   * Requirement 7.5
   */
  const handleConfirmDelete = useCallback(async () => {
    if (!selectedUser) return;

    setIsSubmitting(true);
    setError(null);

    try {
      await restClient.deleteUser(selectedUser.username);

      await fetchUsers();

      setIsDeleteModalOpen(false);
      setSelectedUser(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete user');
    } finally {
      setIsSubmitting(false);
    }
  }, [selectedUser, fetchUsers]);

  if (loading && users.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-primary-50">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-primary-900">User Management</h1>
            </div>
            
            <Button variant="primary" onClick={handleCreateUser}>
              Create User
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 pt-3 pb-8">
        {/* Search Bar */}
        <div className="mb-6">
        <div className="relative">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search users by name, email, username, or rider name..."
            className="w-full px-4 py-2 pl-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
          <svg
            className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      {/* Users Grid */}
      {filteredUsers.length === 0 ? (
        <Card>
          <div className="text-center py-12">
            <svg
              className="mx-auto h-12 w-12 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
              />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900">No users found</h3>
            <p className="mt-1 text-sm text-gray-500">
              {searchQuery ? 'Try a different search query' : 'Get started by creating a new user'}
            </p>
            {!searchQuery && (
              <div className="mt-6">
                <Button variant="primary" onClick={handleCreateUser}>
                  Create User
                </Button>
              </div>
            )}
          </div>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredUsers.map(user => (
            <UserCard
              key={user.username}
              user={user}
              onEdit={handleEditUser}
              onDelete={handleDeleteUser}
              onResetPassword={handleResetPassword}
              onAssociateRider={handleAssociateRider}
              currentSeason={currentSeason}
              allRiders={riders}
            />
          ))}
        </div>
      )}

      {/* User Form Modal */}
      <Modal
        isOpen={isFormModalOpen}
        onClose={() => {
          setIsFormModalOpen(false);
          setSelectedUser(null);
        }}
        title={selectedUser ? 'Edit User' : 'Create User'}
      >
        <UserForm
          user={selectedUser || undefined}
          onSubmit={handleSubmitUser}
          onCancel={() => {
            setIsFormModalOpen(false);
            setSelectedUser(null);
          }}
          isLoading={isSubmitting}
          riders={riders}
          currentSeason={currentSeason}
        />
      </Modal>

      {/* Delete Confirmation Modal */}
      <DeleteConfirmation
        user={selectedUser}
        isOpen={isDeleteModalOpen}
        onConfirm={handleConfirmDelete}
        onCancel={() => {
          setIsDeleteModalOpen(false);
          setSelectedUser(null);
        }}
        isLoading={isSubmitting}
      />

      {/* Associate Rider Modal */}
      <Modal
        isOpen={isAssociateModalOpen}
        onClose={() => {
          setIsAssociateModalOpen(false);
          setSelectedUser(null);
          setSelectedRiderId('');
          setSelectedRole('Parent');
        }}
        title="Associate Rider"
      >
        <div className="space-y-4">
          {selectedUser && (
            <>
              <div>
                <p className="text-sm text-gray-600">
                  <span className="font-medium">User:</span> {selectedUser.name || selectedUser.given_name} ({selectedUser.email})
                </p>
              </div>

              {/* Current Associations */}
              {selectedUser.rider_associations && selectedUser.rider_associations.length > 0 && (
                <div className="border-t pt-4">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Current Associations:</h4>
                  <div className="space-y-2">
                    {selectedUser.rider_associations.map((assoc, index) => (
                      <div key={index} className="flex items-center justify-between bg-gray-50 p-2 rounded">
                        <div className="text-sm">
                          <span className="font-medium">{formatRiderName(assoc)}</span>
                          <span className="text-gray-500"> - {assoc.role}</span>
                        </div>
                        <button
                          onClick={() => handleRemoveAssociation(selectedUser, assoc.rider_id)}
                          className="text-red-600 hover:text-red-800"
                          title="Remove"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Add New Association */}
              <div className="border-t pt-4">
                <h4 className="text-sm font-medium text-gray-700 mb-3">Add New Association:</h4>
                
                <div className="space-y-4">
                  <div>
                    <label htmlFor="rider-select" className="block text-sm font-medium text-gray-700 mb-1">
                      Select Rider
                    </label>
                    <select
                      id="rider-select"
                      value={selectedRiderId}
                      onChange={(e) => setSelectedRiderId(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    >
                      <option value="">-- Select Rider --</option>
                      {riders
                        .filter(rider => 
                          rider.season === currentSeason ||
                          (rider.seasons_active && rider.seasons_active.includes(currentSeason))
                        )
                        .map(rider => (
                        <option key={rider.rider_id} value={rider.rider_id}>
                          {formatRiderName(rider)}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label htmlFor="role-select" className="block text-sm font-medium text-gray-700 mb-1">
                      Role
                    </label>
                    <select
                      id="role-select"
                      value={selectedRole}
                      onChange={(e) => setSelectedRole(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    >
                      <option value="Parent">Parent</option>
                      <option value="Guardian">Guardian</option>
                      <option value="Spouse">Spouse</option>
                      <option value="Sibling">Sibling</option>
                      <option value="Self">Self</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                </div>
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                  {error}
                </div>
              )}

              <div className="flex gap-3 pt-4">
                <Button
                  variant="primary"
                  onClick={handleSubmitAssociation}
                  disabled={isSubmitting || !selectedRiderId}
                  className="flex-1"
                >
                  {isSubmitting ? 'Associating...' : 'Associate'}
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => {
                    setIsAssociateModalOpen(false);
                    setSelectedUser(null);
                    setSelectedRiderId('');
                    setSelectedRole('Parent');
                  }}
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
              </div>
            </>
          )}
        </div>
      </Modal>

      {/* Password Management Modal */}
      {selectedUserForPassword && (
        <PasswordManagementModal
          user={selectedUserForPassword}
          isOpen={isPasswordModalOpen}
          onClose={() => {
            setIsPasswordModalOpen(false);
            setSelectedUserForPassword(null);
          }}
          onSuccess={() => {
            fetchUsers(); // Refresh user list
          }}
        />
      )}
      </div>
    </div>
  );
}

export default Users;
